import React from "react";

interface MessageSkeletonProps {
  count?: number;
  senderRole?: "USER" | "AGENT";
}

const SingleMessageSkeleton: React.FC<{ senderRole: "USER" | "AGENT" }> = ({ senderRole }) => {
  const isUser = senderRole === "USER";
  
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} space-x-2 animate-pulse`}>
      {/* Avatar skeleton */}
      {!isUser && (
        <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" />
      )}
      
      <div className={`max-w-[80%] ${isUser ? "order-first" : ""}`}>
        {/* Message bubble skeleton */}
        <div
          className={`
            p-3 rounded-lg bg-gray-200
            ${isUser 
              ? "bg-gray-100 ml-auto" 
              : "bg-gray-200"
            }
          `}
        >
          {/* Simulate different message lengths */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-300 rounded w-3/4" />
            <div className="h-4 bg-gray-300 rounded w-1/2" />
            {Math.random() > 0.5 && (
              <div className="h-4 bg-gray-300 rounded w-5/6" />
            )}
          </div>
        </div>
        
        {/* Timestamp skeleton */}
        <div className={`flex items-center mt-1 space-x-1 ${isUser ? "justify-end" : "justify-start"}`}>
          <div className="h-3 bg-gray-200 rounded w-16" />
        </div>
      </div>
      
      {/* User avatar skeleton */}
      {isUser && (
        <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" />
      )}
    </div>
  );
};

export const MessageSkeleton: React.FC<MessageSkeletonProps> = ({ 
  count = 3,
  senderRole 
}) => {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }, (_, i) => {
        // Alternate between USER and AGENT if no specific role provided
        const role = senderRole || (i % 2 === 0 ? "AGENT" : "USER");
        return (
          <SingleMessageSkeleton 
            key={`skeleton-${i}`} 
            senderRole={role}
          />
        );
      })}
    </div>
  );
};